import React, { useState, useEffect, useCallback } from 'react';
import { activityApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Phone, Mail, MessageSquare, Plus, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const ACTIVITY_TYPES = ['note', 'call', 'email', 'meeting', 'follow_up'];

const typeIcon = (type) => {
  switch (type) {
    case 'call': return Phone;
    case 'email': return Mail;
    case 'meeting': return Calendar;
    case 'note': return FileText;
    default: return MessageSquare;
  }
};

const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export const ProjectNotes = ({ projectId, leadId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newActivity, setNewActivity] = useState({ type: 'note', description: '' });
  const [adding, setAdding] = useState(false);

  const fetchActivities = useCallback(async () => {
    try {
      const calls = [activityApi.getAll({ project_id: projectId })];
      if (leadId) calls.push(activityApi.getAll({ lead_id: leadId }));
      const results = await Promise.all(calls);
      const projectActs = (results[0].data || []).map((a) => ({ ...a, _source: 'project' }));
      const leadActs = leadId ? (results[1].data || []).map((a) => ({ ...a, _source: 'lead' })) : [];
      const merged = [...projectActs, ...leadActs].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setActivities(merged);
    } catch (e) {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [projectId, leadId]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newActivity.description.trim()) return;
    setAdding(true);
    try {
      const res = await activityApi.create({
        project_id: projectId,
        type: newActivity.type,
        description: newActivity.description.trim(),
      });
      setActivities([{ ...res.data, _source: 'project' }, ...activities]);
      setNewActivity({ type: 'note', description: '' });
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card className="rounded-sm" data-testid="project-notes">
      <CardHeader>
        <CardTitle className="text-lg">Notes &amp; Communications</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleAdd} className="mb-6">
          <div className="flex gap-2 mb-2">
            <Select value={newActivity.type} onValueChange={(val) => setNewActivity({ ...newActivity, type: val })}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="project-note-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder="Add a note, log a call, or record a client communication about this project..."
            value={newActivity.description}
            onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
            className="rounded-sm mb-2"
            rows={3}
            data-testid="project-note-description"
          />
          <Button type="submit" disabled={adding || !newActivity.description.trim()}
            className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="project-note-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Note
          </Button>
        </form>

        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading…</p>
        ) : activities.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No notes yet. Add the first one above.</p>
        ) : (
          <div className="space-y-4">
            {activities.map((a) => {
              const Icon = typeIcon(a.type);
              return (
                <div key={a.id} className="flex gap-4 pb-4 border-b last:border-0" data-testid={`project-note-${a.id}`}>
                  <div className="h-8 w-8 bg-secondary rounded-sm flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-xs capitalize">{(a.type || 'note').replace('_', ' ')}</Badge>
                      {a._source === 'lead' && <Badge variant="secondary" className="text-xs">From lead</Badge>}
                      <span className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">{a.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
